import React, { createContext } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-primitives/tooltip'
import { Provider as SimpleTooltipProvider, TooltipPortalProps } from '@radix-ui/react-tooltip'
import { useContext } from 'react'

export { SimpleTooltipProvider }

/** Helper to make tooltips easy to use */
export function SimpleTooltip(props: {
  /**
   * The tooltip content to render when tooltip is triggered
   * If this is false-y, no tooltip will be rendered at all (only the children)
   */
  tooltip?: string | React.ReactNode | null

  /** The delay for the tooltip to appear (will have no effect if custom provider is used) */
  delayDuration?: number

  /** On which side it should appear */
  side?: "top" | "right" | "bottom" | "left"

  /** The tooltip trigger */
  children: React.ReactNode

  /**
   * Whether this Tooltip should create its own TooltipProvider.
   * Set to `false` when the Tooltip is already wrapped in a shared provider
   * (e.g. toolbars, button groups) to preserve skipDelayDuration behavior.
   * @default true
   */
  withProvider?: boolean

  /** Extra props for radix tooltip Provider */
  providerProps?: Omit<React.ComponentProps<typeof SimpleTooltipProvider>, "children">

  /** Extra props for radix tooltip Root */
  rootProps?: Omit<React.ComponentProps<typeof Tooltip>, "children">

  /** Extra props for radix tooltip Portal */
  portalProps?: TooltipPortalProps

  /** Extra props for radix tooltip Trigger */
  triggerProps?: React.ComponentProps<typeof TooltipTrigger>

  /** Extra props for radix tooltip Content */
  tooltipProps?: React.ComponentProps<typeof TooltipContent>
}) {
  const container = useContext(TooltipPortalContainerContext)
  if (!props.tooltip) return props.children

  return (
    <Tooltip {...props.rootProps}
             withProvider={props.withProvider ?? true}
             providerProps={{
               ...props.providerProps,
               delayDuration: props.delayDuration
             }}>
      <TooltipTrigger {...props.triggerProps} asChild>
        {props.children}
      </TooltipTrigger>
      <TooltipContent side={props.side}
                      labelClassNames="text-center leading-relaxed whitespace-pre-wrap px-2"
                      portalProps={{
                        container,
                        ...props.portalProps
                      }}
                      {...props.tooltipProps}>
          {props.tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

const TooltipPortalContainerContext = createContext<HTMLElement | undefined>(undefined)

/**
 * Makes all children use this container for its portal.
 * Useful inside modals to prevent z-index issues.
 */
export function TooltipPortalContainer(props: {
  container: HTMLElement | undefined
  children: React.ReactNode
}) {
  return (
    <TooltipPortalContainerContext.Provider value={props.container}>
      {props.children}
    </TooltipPortalContainerContext.Provider>
  )
}
